import { useLocation } from 'react-router-dom';
import DatePickerComp, {
    ReactDatePickerCustomHeaderProps,
    ReactDatePickerProps,
    registerLocale,
} from 'react-datepicker';
import { getYear, getMonth } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { convertLocalToUTCDate, convertUTCToLocalDate } from '../../WidgetUtils';
import { useLocalization } from '../../hooks';

export const CustomHeader = (
    props: ReactDatePickerCustomHeaderProps & { locale: string; i18n: any }
) => {
    const {
        date,
        changeYear,
        changeMonth,
        decreaseYear,
        increaseYear,
        prevYearButtonDisabled,
        nextYearButtonDisabled,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        locale,
        i18n,
    } = props;

    // Function to generate an array of years
    const getYears = (start: number, end: number): number[] => {
        return Array.from({ length: end - start }, (_, i) => i + start);
    };

    // Function to generate an array of localized month names
    const getMonths = (lang: Intl.LocalesArgument) => {
        return Array.from({ length: 12 }, (_, i) => {
            // Create a new Date object for each month
            const date = new Date(2021, i, 1);
            const options: Intl.DateTimeFormatOptions = { month: 'long' };
            return date.toLocaleDateString(lang, options);
        });
    };

    // Generate an array of years from 1960 to the current year + 1
    const years: number[] = getYears(1960, new Date().getFullYear() + 1);

    // Generate an array of localized month names
    const months: string[] = getMonths(locale);

    return (
        <div
            style={{
                margin: 10,
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            {/* Button to decrease the year */}
            <button
                className="decrease-year"
                aria-label={i18n.t('decrease_year')}
                onClick={decreaseYear}
                disabled={getYear(date) === years?.[0] || prevYearButtonDisabled}
                tabIndex={0}
            >
                {'<'}
            </button>

            {/* Select input to choose the year */}
            <select
                className="custom-react-date-picker-select year-select"
                value={getYear(date)}
                onChange={({ target: { value } }) => changeYear(value as any)}
                aria-label={i18n.t('year')}
                tabIndex={0}
            >
                {years?.map((option) => (
                    <option
                        className="custom-react-date-picker-select-option"
                        key={option}
                        value={option}
                    >
                        {option}
                    </option>
                ))}
            </select>

            {/* Button to increase the year */}
            <button
                className="increase-year"
                onClick={increaseYear}
                disabled={getYear(date) === years?.[years?.length - 1] || nextYearButtonDisabled}
                aria-label={i18n.t('increase_year')}
                tabIndex={0}
            >
                {'>'}
            </button>

            {/* Button to decrease the month */}
            <button
                className="decrease-month"
                onClick={decreaseMonth}
                disabled={
                    (months?.[getMonth(date)] === months?.[0] && getYear(date) === years?.[0]) ||
                    prevMonthButtonDisabled
                }
                aria-label={i18n.t('decrease_month')}
                tabIndex={0}
            >
                {'<'}
            </button>

            {/* Select input to choose the month */}
            <select
                className="custom-react-date-picker-select month-select"
                value={months?.[getMonth(date)]}
                onChange={({ target: { value } }) => changeMonth(months?.indexOf(value))}
                aria-label={i18n.t('month')}
                tabIndex={0}
            >
                {months?.map((option) => (
                    <option
                        key={option}
                        className="custom-react-date-picker-select-option"
                        value={option}
                    >
                        {option}
                    </option>
                ))}
            </select>

            {/* Button to increase the month */}
            <button
                className="increase-month"
                onClick={increaseMonth}
                disabled={
                    (months?.[getMonth(date)] === months?.[months?.length - 1] &&
                        getYear(date) === years?.[years?.length - 1]) ||
                    nextMonthButtonDisabled
                }
                aria-label={i18n.t('increase_month')}
                tabIndex={0}
            >
                {'>'}
            </button>
        </div>
    );
};

export interface DateFilterOptions extends ReactDatePickerProps {
    filteredDateArray?: Date[] | undefined;
}

export const DatePicker = (props: DateFilterOptions) => {
    const {
        selected,
        dateFormat,
        customInput,
        minDate,
        maxDate,
        filteredDateArray,
        onChange,
        ...rest
    } = props;
    const location = useLocation();
    const search = location.search;
    const params = new URLSearchParams(search);
    const locale: string = params.get('locale') || 'en-US';
    const i18n = useLocalization();

    // Register locale for react-datepicker to see dates in particular locale
    import(`date-fns/locale`).then((supportedLocales) => {
        const supportedLocale = (supportedLocales[locale] ||
            supportedLocales[locale.replace('-', '')]) as Locale;
        registerLocale(locale, supportedLocale);
    });

    // Render days for date picker
    const renderDayContents = (day: any, date: any) => {
        return (
            <span
                tabIndex={0}
                onKeyDown={(e: any) =>
                    e.key === 'Enter' ? onChange(convertLocalToUTCDate(date), e) : null
                }
            >
                {day}
            </span>
        );
    };

    // Define filter function only when filteredDateArray is not empty
    const filterDate =
        filteredDateArray && filteredDateArray.length > 0
            ? (date: Date) => {
                  const day = date.getDate();
                  const month = date.getMonth();
                  const year = date.getFullYear();
                  return filteredDateArray.some(
                      (d) =>
                          d.getDate() === day && d.getMonth() === month && d.getFullYear() === year
                  );
              }
            : undefined;

    return (
        <DatePickerComp
            renderCustomHeader={(props) => <CustomHeader {...props} locale={locale} i18n={i18n} />}
            locale={locale}
            selected={convertUTCToLocalDate(selected as Date)}
            onChange={(date: Date, e: any) => {
                onChange(convertLocalToUTCDate(date), e);
            }}
            dateFormat={dateFormat}
            customInput={customInput}
            minDate={convertUTCToLocalDate(minDate as Date)}
            maxDate={convertUTCToLocalDate(maxDate as Date)}
            closeOnScroll={rest.closeOnScroll}
            formatWeekDay={(s: string) =>
                s.slice(0, Number(rest.formatWeekDay) ? Number(rest.formatWeekDay) : 2)
            }
            dayClassName={() =>
                Number(rest.formatWeekDay) >= 4 ? 'custom-calendar-day-width' : ''
            }
            weekDayClassName={() =>
                Number(rest.formatWeekDay) >= 4 ? 'custom-calendar-day-width' : ''
            }
            enableTabLoop={false}
            renderDayContents={renderDayContents}
            filterDate={filterDate}
        />
    );
};
